import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, Paperclip, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import Segmented from "@/components/Segmented";
import { preconApi } from "../lib/api";

export const BID_FOLDERS = [
  { key: "bid_documents", label: "ITB / Plans & Specs" },
  { key: "addenda", label: "Addenda / Changes" },
  { key: "rfis", label: "RFIs" },
  { key: "prevailing_wage", label: "Prevailing Wage" },
  { key: "correspondence", label: "Correspondence" },
];
const LABEL = Object.fromEntries(BID_FOLDERS.map((f) => [f.key, f.label]));
const MAX = 25 * 1024 * 1024;
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

// Bid Files: drop ITBs, addenda, RFIs, wage rates… anywhere on the panel. Files go straight to
// object storage (one upload at a time, off the page's main work); the list only loads names.
export default function BidFilesTab({ job }) {
  const [folder, setFolder] = useState("all");
  const [docs, setDocs] = useState(null);
  const [counts, setCounts] = useState({});
  const [uploads, setUploads] = useState([]);
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const target = folder === "all" ? "bid_documents" : folder;

  const load = useCallback(() => preconApi.listDocuments(job.id).then((d) => { setDocs(d.documents); setCounts(d.counts || {}); })
    .catch(() => setDocs([])), [job.id]);
  useEffect(() => { load(); }, [load]);

  const send = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const dest = target;
    setUploads(list.map((f) => ({ name: f.name, pct: 0, err: f.size > MAX ? "Over 25 MB" : "" })));
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].size > MAX) continue;
      try {
        await preconApi.uploadDocument(job.id, list[i], dest, (pct) => setUploads((u) => u.map((x, k) => (k === i ? { ...x, pct } : x))));
      } catch (e) {
        setUploads((u) => u.map((x, k) => (k === i ? { ...x, err: e?.response?.data?.detail || "Upload failed" } : x)));
      }
    }
    const failed = list.filter((f) => f.size > MAX).length;
    toast.success(`Filed ${list.length - failed} file(s) in ${LABEL[dest]}`);
    setTimeout(() => setUploads([]), 1500);
    load();
  };

  const open = async (d) => {
    try {
      const blob = await preconApi.downloadDocument(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Could not download that file"); }
  };

  const rows = (docs || []).filter((d) => folder === "all" || d.folder === folder);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <div className="max-w-5xl space-y-3" data-testid="bid-files-tab"
         onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
         onDrop={(e) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files); }}>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented value={folder} onChange={setFolder}
                   options={[{ key: "all", label: "All", count: total || undefined, testId: "bid-files-folder-all" },
                             ...BID_FOLDERS.map((f) => ({ ...f, count: counts[f.key] || undefined, testId: `bid-files-folder-${f.key}` }))]} />
        <Button variant="data" size="sm" className="ml-auto h-8 text-xs" onClick={() => inputRef.current?.click()} data-testid="bid-files-upload-btn">
          <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload
        </Button>
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => { send(e.target.files); e.target.value = ""; }} data-testid="bid-files-input" />
      </div>

      <button type="button" onClick={() => inputRef.current?.click()} data-testid="bid-files-dropzone"
              className={`flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-5 text-xs transition-colors ${over ? "border-[#007BFF] bg-blue-50 text-[#007BFF] dark:bg-blue-950/40" : "border-slate-300 text-slate-500 hover:border-slate-400 dark:border-slate-600"}`}>
        <Upload className="mb-1 h-4 w-4" />
        Drop files here — they go into <strong className="ml-1">{LABEL[target]}</strong>
        <span className="mt-0.5 text-[10px] opacity-70">Up to 25 MB each · same name in the same folder keeps the old one as a version</span>
      </button>

      {uploads.length > 0 && (
        <ul className="space-y-1" data-testid="bid-files-progress">
          {uploads.map((u, i) => (
            <li key={i} className="flex items-center gap-2 text-[11px]">
              <span className="w-64 truncate">{u.name}</span>
              {u.err ? <span className="text-red-600">{u.err}</span>
                : <span className="h-1.5 flex-1 overflow-hidden rounded bg-slate-200 dark:bg-slate-700"><span className="block h-full bg-[#007BFF] transition-[width]" style={{ width: `${u.pct}%` }} /></span>}
            </li>))}
        </ul>)}

      {docs === null ? (
        <div className="flex items-center gap-2 py-6 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading files…</div>
      ) : !rows.length ? (
        <div className="py-4 text-xs text-slate-500" data-testid="workspace-files-empty">No files here yet.</div>
      ) : (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-card dark:divide-slate-700 dark:border-slate-700" data-testid="workspace-files">
          {rows.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`workspace-file-${d.id}`}>
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1 truncate font-medium">{d.file_name}</span>
              {d.version > 1 && <span className="rounded bg-slate-100 px-1 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">v{d.version}</span>}
              <span className="w-36 shrink-0 text-slate-500">{LABEL[d.folder] || d.folder}</span>
              <span className="w-16 shrink-0 text-right text-slate-500">{size(d.size || 0)}</span>
              <span className="w-24 shrink-0 text-slate-500">{when(d.created_at)}</span>
              <Button variant="data" size="sm" className="h-7 w-7 p-0" onClick={() => open(d)} title="Download" data-testid={`workspace-file-download-${d.id}`}>
                <Download className="h-3.5 w-3.5" />
              </Button>
            </li>))}
        </ul>)}
    </div>
  );
}
